import React from 'react';
import axios from 'axios';
import CryptoJS from 'crypto-js'
import { baseURL } from '../API/url'
import { sessionManager, setupAxiosInterceptors } from '../utils/sessionManager';
import '../App.css';
import {
  	HashRouter as Router,
  	Route,
  	Link,
	Redirect
} from 'react-router-dom';
import { withRouter } from "react-router-dom";
import { Form, Input, Button, Checkbox, Row, Col, Alert, Modal, message} from 'antd';
import { UserOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';

class Login extends React.Component<any,any> {
	constructor(props:any) {
	    super(props);
		
		// Setup axios interceptors for session management
		setupAxiosInterceptors(axios);
		
		const rememberMe = localStorage.getItem('rememberMe') === 'true';
    	const email = rememberMe ? localStorage.getItem('email') : '';
		
		// Check if redirected due to session expiration
		const sessionExpired = localStorage.getItem('session_expired') === 'true';
		if (sessionExpired) {
			// Clear the flag
			localStorage.removeItem('session_expired');
		}

	    this.state = { 
			data:{ email: email, password:'' },
			isAuthenticated: props.isAuthenticated,
			rememberMe:rememberMe,
			sessionExpiredMessage: sessionExpired ? 'Your current session was expired. Please login again' : '',
			// IP Verification modal state
			showVerificationModal: false,
			verificationCode: '',
			pendingUserId: null,
			verificationMessage: '',
			verifying: false,
		 };
	}

	// Cookie utility functions
	setCookie = (name: string, value: string, days: number = 7) => {
		const expires = new Date();
		expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
		document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
	}

	getCookie = (name: string): string | null => {
		const nameEQ = name + "=";
		const ca = document.cookie.split(';');
		for(let i = 0; i < ca.length; i++) {
			let c = ca[i];
			while (c.charAt(0) === ' ') c = c.substring(1, c.length);
			if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
		}
		return null;
	}

	deleteCookie = (name: string) => {
		document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
	}

	componentDidMount = () => {
		// Check if user is already authenticated via cookie
		const token = this.getCookie('x-auth-token');
		if (token && !this.state.isAuthenticated) {
			console.log('Found existing auth token, validating...');
			// Set the token in axios headers
			axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
			
			// Validate the token with the server
			axios.get(baseURL + "/auth/isAuthenticated")
				.then((response) => {
					if (response.data && response.data.email) {
						console.log('Token is valid, auto-authenticating user');
						this.props.setAuthenticate(true);
						this.props.setCurrentUser(response.data.email);
						
/* 						// Redirect based on user type
						if (response.data.userType === 'emd') {
							this.props.history.push('/emd-portfolio');
						} else {
							this.props.history.push('/clients');
						}
 */					}
				})
				.catch((error) => {
					console.log('Token validation failed:', error);
					// Clear invalid token
					this.deleteCookie('x-auth-token');
					axios.defaults.headers.common['Authorization'] = '';
				});
		}
	}


	onFinish = async (values: any) => {
		const secretKey = process.env.REACT_APP_SECRET_KEY;
		  const encryptedEmail = CryptoJS.AES.encrypt(
			JSON.stringify(values.username.toLowerCase()),
			secretKey
		  ).toString();

		  const encryptedPassword = CryptoJS.AES.encrypt(
			JSON.stringify(values.password),
			secretKey
		  ).toString();

		  this.setState({
			data:{email:values.username.toLowerCase(), password: encryptedPassword},
			rememberMe: values.remember,
		  });		
		  
		try {
			// Use session-based login instead of JWT
			const loginResult = await sessionManager.login(values.username.toLowerCase(), encryptedPassword);
			
			// Check if IP verification is required
			if (loginResult.requiresIpVerification) {
				console.log('IP verification required, showing modal');
				this.setState({
					showVerificationModal: true,
					pendingUserId: loginResult.userId,
					verificationMessage: loginResult.message || 'A verification code has been sent to your email.',
				});
				return;
			}
			
			if (loginResult.success) {
				this.completeLogin(values.username);
			} else {
				alert(loginResult.message || 'Invalid credentials. Please try again.');
			}
		} catch (error) {
			console.error('Login error:', error);
			console.error('Error details:', error instanceof Error ? error.stack : String(error));
			alert('Login failed. Please try again.');
		}
	};

	// Handle verification code submission
	handleVerificationSubmit = async () => {
		const { verificationCode, pendingUserId, data } = this.state;
		
		if (!verificationCode || verificationCode.length !== 6) {
			message.error('Please enter a valid 6-digit verification code');
			return;
		}
		
		this.setState({ verifying: true });
		
		try {
			const result = await sessionManager.verifyIpChange(pendingUserId, verificationCode);
			
			if (result.success) {
				message.success('Verification successful!');
				this.setState({
					showVerificationModal: false,
					verificationCode: '',
					pendingUserId: null,
				});
				this.completeLogin(data.email);
			} else {
				message.error(result.message || 'Verification failed. Please try again.');
				if (!result.requiresIpVerification) {
					// Verification system error, close modal
					this.setState({
						showVerificationModal: false,
						verificationCode: '',
						pendingUserId: null,
					});
				}
			}
		} catch (error) {
			console.error('Verification error:', error);
			message.error('Verification failed. Please try again.');
		} finally {
			this.setState({ verifying: false });
		}
	};

	// Cancel verification
	handleVerificationCancel = () => {
		this.setState({
			showVerificationModal: false,
			verificationCode: '',
			pendingUserId: null,
			verificationMessage: '',
		});
	};

	// Complete login after successful authentication or verification
	completeLogin = async (username: string) => {
		console.log('Session-based login successful');
		
		// Verify that the session token is properly set in axios headers
		const sessionToken = sessionManager.getSessionToken();
		if (sessionToken) {
			console.log('Session token set successfully:', sessionToken.substring(0, 10) + '...');
		} else {
			console.error('Warning: No session token found after login');
		}
		
		// Get user data from session FIRST before setting authentication
		const sessionData = localStorage.getItem('ginkgo_session');
		console.log('Raw session data from localStorage:', sessionData);
		
		let userTypeFromSession: string | null = null;
		
		if (sessionData) {
			const parsed = JSON.parse(sessionData);
			console.log('Parsed session data:', parsed);
			const userData = parsed.user;
			
			console.log('User data from session:', userData);
			
			if (userData && userData.userType) {
				userTypeFromSession = userData.userType;
				console.log('Found userType in session:', userTypeFromSession);
			}
		}
		
		// If no userType in session, get it from backend
		if (!userTypeFromSession) {
			console.warn('No user data in session, attempting to fetch from backend...');
			try {
				// Make a request to get current user info using session token
				const userResponse = await axios.post(`${baseURL}/auth/validate-session`);
				if (userResponse.data && userResponse.data.user) {
					const backendUserData = userResponse.data.user;
					console.log('User data retrieved from backend:', backendUserData);
					userTypeFromSession = backendUserData.userType;
				}
			} catch (error) {
				console.error('Failed to fetch user data from backend:', error);
				alert('Login successful but unable to retrieve user information. Please try again.');
				return;
			}
		}
		
		// Set current user and authentication state together to prevent multiple re-renders
		console.log('Setting authentication state with userType:', userTypeFromSession);
		console.log('setAuthenticationState method available:', !!this.props.setAuthenticationState);
		
		// Use the new method to set all authentication state in one call
		if (this.props.setAuthenticationState) {
			console.log('Using setAuthenticationState method');
			this.props.setAuthenticationState(true, username, userTypeFromSession);
		} else {
			console.log('Falling back to separate calls');
			// Fallback to separate calls if the new method isn't available
			this.props.setCurrentUser(username);
			this.props.setAuthenticate(true, userTypeFromSession);
		}

		// Remember me functionality
		const { data, rememberMe } = this.state;
		localStorage.setItem('rememberMe', rememberMe.toString());
		localStorage.setItem('email', rememberMe ? data.email : '');
		
		// Don't manually navigate - let the automatic redirect in App component handle routing
		// This prevents double renders from both automatic redirect and manual navigation
		console.log('Authentication state set - automatic redirect will handle navigation to:', userTypeFromSession === 'emd' ? '/emd-portfolio' : userTypeFromSession === 'admin' ? '/admin-portfolio' : '/clients');
		console.log('Navigation completed successfully');
	};

	render(){
		const { showVerificationModal, verificationMessage, verifying, verificationCode } = this.state;
		
		return (
				<Row justify="center" style={{ marginTop: '50px' }}>
      			<Col span={12}>
				{this.state.sessionExpiredMessage && (
					<Alert
						message={this.state.sessionExpiredMessage}
						type="warning"
						showIcon
						closable
						style={{ marginBottom: '20px' }}
					/>
				)}
				<Form
				layout='vertical'
				name="normal_login"
				className="login-form"
				initialValues={{username: this.state.data.email, remember: this.state.rememberMe }}
				onFinish={this.onFinish}
			>
				<Form.Item
				label="Username:"
				name="username"
				rules={[{ required: true, message: 'Please input your email!' }]}
				>
				<Input prefix={<UserOutlined className="site-form-item-icon" />} placeholder="Email" />
				</Form.Item>
				<Form.Item
				label="Password:"
				name="password"
				rules={[{ required: true, message: 'Please input your Password!' }]}
				hasFeedback
				>
				<Input.Password />
				</Form.Item>
				<Form.Item>
				<Form.Item name="remember" valuePropName="checked" noStyle>
					<Checkbox>
						Remember me
					</Checkbox>
				</Form.Item>
		
				<Link className="login-form-forgot" to="/resetPassword">
					Forgot password
				</Link>
				</Form.Item>
		
				<Form.Item>
				<Button type="primary" htmlType="submit" className="login-form-button">
					Login								   
				</Button>
				&nbsp; Or <a href="/register">Register now!</a>
				</Form.Item>
			</Form>
			
			{/* IP Verification Modal */}
			<Modal
				title={
					<span>
						<SafetyOutlined style={{ color: '#1890ff', marginRight: 8 }} />
						New Login Location Detected
					</span>
				}
				open={showVerificationModal}
				onOk={this.handleVerificationSubmit}
				onCancel={this.handleVerificationCancel}
				okText="Verify"
				cancelText="Cancel"
				confirmLoading={verifying}
				closable={!verifying}
				maskClosable={!verifying}
			>
				<Alert
					message="Security Verification Required"
					description={verificationMessage}
					type="info"
					showIcon
					style={{ marginBottom: 20 }}
				/>
				<p style={{ marginBottom: 10 }}>
					Please enter the 6-digit verification code sent to your email:
				</p>
				<Input
					prefix={<SafetyOutlined />}
					placeholder="Enter 6-digit code"
					maxLength={6}
					value={verificationCode}
					onChange={(e) => this.setState({ verificationCode: e.target.value.replace(/\D/g, '') })}
					onPressEnter={this.handleVerificationSubmit}
					style={{ 
						fontSize: '20px', 
						letterSpacing: '8px', 
						textAlign: 'center',
						fontWeight: 'bold'
					}}
				/>
				<p style={{ marginTop: 15, color: '#666', fontSize: '12px' }}>
					The verification code will expire in 10 minutes. If you didn't receive the email, 
					please check your spam folder or try logging in again.
				</p>
			</Modal>
			</Col>
    </Row>
		)
	}
}
export default withRouter(Login);




